import { Box, Chip, Stack } from '@mui/material'
import { type FC } from 'react'
import { gql } from '__generated__'
import { type CandidateFilterState } from 'shared/hooks/candidateSearchUrlState'
import {
    FilterWrapper,
    IdChipFilter,
    ExcludeIdChipFilter,
    ExcludeIdFilter,
} from './FilterPrimitives'
import { HometownFilter } from './CandidateMinimalFilters'
import { toggleChipSx } from './minimalFilterStyles'

const GetGenderOptions = gql(/* GraphQL */ `
    query GetGenderOptionsMinimal {
        genders {
            id
            value
        }
    }
`)

const GetEthnicityOptions = gql(/* GraphQL */ `
    query GetEthnicityOptionsMinimal {
        ethnicities {
            id
            value
        }
    }
`)

const GetCitizenshipOptions = gql(/* GraphQL */ `
    query GetCitizenshipOptionsMinimal {
        citizenships {
            id
            value
        }
    }
`)

const GetVisaStatusOptions = gql(/* GraphQL */ `
    query GetVisaStatusOptionsMinimal {
        visaStatuses {
            id
            value
        }
    }
`)

const GetQualitativeOptions = gql(/* GraphQL */ `
    query GetQualitativeOptionsMinimal {
        personalityAttributes {
            id
            value
        }
    }
`)

const GetRevenueExperienceOptions = gql(/* GraphQL */ `
    query GetRevenueExperienceOptionsMinimal {
        revenueExperiences {
            id
            value
        }
    }
`)

const GetLgbtqOptions = gql(/* GraphQL */ `
    query GetLgbtqOptionsMinimal {
        lgbtq {
            id
            value
        }
    }
`)

const GetCpiPlacedOptions = gql(/* GraphQL */ `
    query GetCpiPlacedOptionsMinimal {
        cpiPlaced {
            id
            value
        }
    }
`)

const GetReferenceAttributeOptions = gql(/* GraphQL */ `
    query GetReferenceAttributeOptionsMinimal {
        referenceAttributes {
            id
            value
        }
    }
`)

const GetMbaTierOptions = gql(/* GraphQL */ `
    query GetMbaTierOptionsMinimal {
        mbaTiers {
            id
            value
        }
    }
`)

interface AttributesFilterSectionProps {
    filters: CandidateFilterState
    updateFilters: (patch: Partial<CandidateFilterState>) => void
}

export const AttributesFilterSection: FC<AttributesFilterSectionProps> = ({
    filters,
    updateFilters,
}) => {
    return (
        <Stack gap={2.5} sx={{ p: 2 }}>
            <IdChipFilter
                label="Gender"
                query={GetGenderOptions}
                extractOptions={(d) => d?.genders ?? []}
                value={filters.genderIds}
                onChange={(genderIds) => updateFilters({ genderIds })}
            />
            <IdChipFilter
                label="Race/Ethnicity"
                query={GetEthnicityOptions}
                extractOptions={(d) => d?.ethnicities ?? []}
                value={filters.ethnicityIds}
                onChange={(ethnicityIds) => updateFilters({ ethnicityIds })}
            />
            <IdChipFilter
                label="Citizenship"
                query={GetCitizenshipOptions}
                extractOptions={(d) => d?.citizenships ?? []}
                value={filters.citizenshipIds}
                onChange={(citizenshipIds) => updateFilters({ citizenshipIds })}
            />
            <ExcludeIdFilter
                label="Visa Status"
                query={GetVisaStatusOptions}
                extractOptions={(d) => d?.visaStatuses ?? []}
                value={filters.visaStatusIds}
                valueNot={filters.visaStatusIdsNot}
                onChange={(visaStatusIds, visaStatusIdsNot) =>
                    updateFilters({ visaStatusIds, visaStatusIdsNot })
                }
            />
            <ExcludeIdFilter
                label="Qualitative"
                query={GetQualitativeOptions}
                extractOptions={(d) => d?.personalityAttributes ?? []}
                value={filters.qualitativeIds}
                valueNot={filters.qualitativeIdsNot}
                onChange={(qualitativeIds, qualitativeIdsNot) =>
                    updateFilters({ qualitativeIds, qualitativeIdsNot })
                }
            />
            <IdChipFilter
                label="Revenue Experience"
                query={GetRevenueExperienceOptions}
                extractOptions={(d) => d?.revenueExperiences ?? []}
                value={filters.revenueExperienceIds}
                onChange={(revenueExperienceIds) =>
                    updateFilters({ revenueExperienceIds })
                }
            />
            <IdChipFilter
                label="LGBTQ"
                query={GetLgbtqOptions}
                extractOptions={(d) => d?.lgbtq ?? []}
                value={filters.lgbtqIds}
                onChange={(lgbtqIds) => updateFilters({ lgbtqIds })}
            />
            <ExcludeIdChipFilter
                label="CPI Placed"
                query={GetCpiPlacedOptions}
                extractOptions={(d) => d?.cpiPlaced ?? []}
                value={filters.cpiPlacedIds}
                valueNot={filters.cpiPlacedIdsNot}
                onChange={(cpiPlacedIds, cpiPlacedIdsNot) =>
                    updateFilters({ cpiPlacedIds, cpiPlacedIdsNot })
                }
            />
            <IdChipFilter
                label="Reference Attributes"
                query={GetReferenceAttributeOptions}
                extractOptions={(d) => d?.referenceAttributes ?? []}
                value={filters.referenceAttributeIds}
                onChange={(referenceAttributeIds) =>
                    updateFilters({ referenceAttributeIds })
                }
            />
            <HometownFilter
                value={filters.hometownSearchTerms}
                onChange={(hometownSearchTerms) =>
                    updateFilters({ hometownSearchTerms })
                }
            />
            <IdChipFilter
                label="MBA Tier"
                query={GetMbaTierOptions}
                extractOptions={(d) => d?.mbaTiers ?? []}
                value={filters.mbaTierIds}
                onChange={(mbaTierIds) => updateFilters({ mbaTierIds })}
            />
            <FilterWrapper label="Status">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                        label="Include Deactivated"
                        size="small"
                        onClick={() =>
                            updateFilters({
                                includeDeactivated: !filters.includeDeactivated,
                            })
                        }
                        sx={toggleChipSx(filters.includeDeactivated)}
                    />
                </Box>
            </FilterWrapper>
        </Stack>
    )
}
